import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import Store from 'store/store';
import Grid from '@material-ui/core/Grid';
import { formatPercent, unFormatPercent } from 'utils';
import { getPercentageObject, resetTableState, getStringObject } from './utils';
import EditablePercent from 'components/Base/EditablePercent/EditablePercent';
import { reduxForm, getFormValues } from 'redux-form';
import api from 'api';
import { useDebouncedCallback } from 'use-debounce';
import { Button, Modal, BoxWrapper } from 'components/Base';
import PersistentTable from './PersistentTable';
import { MRT_ColumnDef } from 'material-react-table';

const { selectors, actions } = Store;

const ChargesTabContent = ({
  selectedInvestment,
  investments,
  selectedInvestments,
  updateResearchInvestments,
  initialValues,
  formValues
}: any) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [rowData, setRowData] = useState([] as any[]);
  const [selectedMpName, setSelectedMpName] = useState('');
  const [parentMpName, setParentMpName] = useState('');
  const [parentRowData, setParentRowData] = useState([] as any[]);
  const [modelPortfolioFunds, setModelPortfolioFunds] = useState([] as any[]);
  const [nestedModelPortfolios, setNestedModelPortfolios] = useState(
    [] as any[]
  );

  useEffect(() => {
    setModelPortfolioFunds(
      rowData.filter((item: any) => !!item.fund || item.type === 'f')
    );
    setNestedModelPortfolios(
      rowData.filter(
        (item: any) => ('key' in item && !item.fund) || item.type === 'mp'
      )
    );
  }, [rowData]);

  const goBackToParentMp = () => {
    setSelectedMpName(parentMpName);
    setRowData(parentRowData);
    setParentMpName('');
    setParentRowData([]);
  };

  const updateMpCharge = useDebouncedCallback((id: number, charges: any) => {
    api.updateModelPortfolioCharge(selectedInvestment, id, charges);
  }, 1000);

  const updateMp = (id: number, charges: any) => {
    const index = investments.findIndex(
      (investment: any) => investment.id === id
    );

    const mappedCharges = charges;
    Object.keys(mappedCharges).forEach((key: any) => {
      mappedCharges[key] =
        mappedCharges[key] === null ? null : Number(mappedCharges[key]);
    });

    const updatedInvestments = [...investments];
    updatedInvestments.splice(index, 1, {
      ...updatedInvestments[index],
      ...mappedCharges
    });
    updateResearchInvestments(updatedInvestments);
    updateMpCharge(id, charges);
  };

  const editableCharge = (
    row: any,
    key: string,
    originalValue: string | null = null
  ) => {
    const defaultValue = originalValue || row[key];

    return (
      <div style={{ display: 'flex', width: '100%' }}>
        <EditablePercent
          formName="researchCharges"
          name={`investments.${row.original.index}.${key}`}
          defaultValue={defaultValue || 0}
          onChange={(e: any) => {
            const charges = {} as any;
            charges[key] = unFormatPercent(e.target.value);
            updateMp(row.original.id, charges);
          }}
          onUnset={() => {
            const charges = {} as any;
            charges[key] = null;
            updateMp(row.original.id, charges);
          }}
        />
      </div>
    );
  };

  const [count, setCount] = useState(0);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        ...getStringObject('name')
      },
      {
        accessorKey: 'initial_charge',
        header: 'Initial Charge',
        ...getPercentageObject('initial_charge'),
        Cell: ({ row }) => {
          if (!selectedInvestment) {
            return formatPercent(
              Number(row.original.original_initial_charge * 100)
            );
          }
          return editableCharge(
            row,
            'initial_charge',
            row.original.original_initial_charge
          );
        }
      },
      {
        accessorKey: 'ocf',
        header: 'OCF',
        ...getPercentageObject('ocf'),
        Cell: ({ row }) => {
          if (!selectedInvestment) {
            return formatPercent(Number(row.original.original_ocf * 100));
          }
          return editableCharge(row, 'ocf', row.original.original_ocf);
        }
      },
      {
        accessorKey: 'trading_costs',
        header: 'Transaction Costs',
        ...getPercentageObject('trading_costs'),
        Cell: ({ row }) => {
          if (!selectedInvestment) {
            return formatPercent(
              Number(row.original.original_trading_costs * 100)
            );
          }
          return editableCharge(
            row,
            'trading_costs',
            row.original.original_trading_costs
          );
        }
      },
      {
        accessorKey: 'model_portfolio_charge',
        header: 'Portfolio Management Charge',
        type: 'number',
        Cell: ({ row }) => {
          if (!selectedInvestment) {
            return formatPercent(
              Number(row.original.model_portfolio_charge * 100)
            );
          }
          return row.original.mp_id
            ? editableCharge(
                row,
                'model_portfolio_charge',
                row.original.mps_charge.charge || '-'
              )
            : '-';
        },
        accessorFn: (row: any) =>
          formatPercent(Number(row.model_portfolio_charge))
      },
      {
        accessorKey: 'totalFundCharge',
        header: 'Total Charges',
        ...getPercentageObject('totalFundCharge')
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div style={{ display: 'flex' }}>
            {row.original.mp_id ? 'Portfolio - ' : 'Fund'}
            {row.original.mp_id && (
              <div
                style={{
                  justifyContent: 'center',
                  fontWeight: '600',
                  color: '#4469a6',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                &nbsp; View
              </div>
            )}
          </div>
        )
      }
    ],
    []
  );

  const mpColumns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        ...getStringObject('name')
      },
      {
        accessorKey: 'allocation',
        header: '%',
        ...getPercentageObject('allocation')
      },
      {
        accessorKey: 'initial_charge',
        header: 'Initial Charge',
        ...getPercentageObject('initial_charge')
      },
      {
        accessorKey: 'ocf',
        header: 'OCF',
        ...getPercentageObject('ocf')
      },
      {
        accessorKey: 'trading_costs',
        header: 'Transaction Costs',
        ...getPercentageObject('trading_costs')
      },
      {
        accessorKey: 'totalFundCharge',
        header: 'Total Charges',
        ...getPercentageObject('totalFundCharge')
      },
      {
        accessorKey: 'type',
        header: 'Type',
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div style={{ display: 'flex' }}>
            {row.original.mp_id ? 'Portfolio - ' : 'Fund'}
            {row.original.mp_id && (
              <div
                style={{
                  justifyContent: 'center',
                  fontWeight: '600',
                  color: '#4469a6',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                &nbsp; View
              </div>
            )}
          </div>
        )
      }
    ],
    []
  );

  const calculateTotalFundCharge = (
    fund: any,
    ocf: number | null = null,
    tradingCosts: number | null = null,
    modelPortfolioCharge: number | null = null
  ) => {
    return fund.total_fund_charge
      ? Number(fund.total_fund_charge)
      : (tradingCosts || Number(fund.original_trading_costs)) +
          (ocf || Number(fund.original_ocf)) +
          Number(
            fund.mp_id
              ? modelPortfolioCharge === null
                ? fund.mps_charge.charge
                : modelPortfolioCharge
              : 0
          );
  };

  const getCharge = (fund: any, index: number, key: string) => {
    const baseCharge =
      (formValues || initialValues)?.investments[index]?.[key] || fund[key];
    return baseCharge !== null ? unFormatPercent(baseCharge) / 100 : null;
  };

  const data = useMemo(() => {
    return selectedInvestments.map((fund: any, index: number) => {
      const modelPortfolioCharge = getCharge(
        fund,
        index,
        'model_portfolio_charge'
      );

      return {
        ...fund,
        index,
        model_portfolio_charge: Number(unFormatPercent(modelPortfolioCharge)),
        totalFundCharge: calculateTotalFundCharge(
          fund,
          getCharge(fund, index, 'ocf'),
          getCharge(fund, index, 'trading_costs'),
          modelPortfolioCharge
        ),
        fundAllocations: fund.fund_allocations
          ? fund.fund_allocations.map(
              (fundAllocation: {
                total_fund_charge: any;
                initial_charge: any;
                trading_costs: any;
                ocf: any;
              }) => {
                const totalFundCharge2 = fundAllocation.total_fund_charge
                  ? Number(fundAllocation.total_fund_charge)
                  : Number(fundAllocation.trading_costs) +
                    Number(fundAllocation.ocf);

                return {
                  ...fundAllocation,
                  totalFundCharge: totalFundCharge2
                };
              }
            )
          : []
      };
    });
  }, [selectedInvestments]);

  const handleRowClick = (params: any, map = false) => {
    if (
      (params.row.original.mp_id && params.column.id === 'type') ||
      params.column.id === 'name'
    ) {
      setModalOpen(true);
      if (params.row && params.row.original.name) {
        setSelectedMpName(params.row.original.name);
      }

      if (map) {
        setParentMpName(selectedMpName);
        setParentRowData(rowData);
        setRowData(
          params.row.original.fund_allocations.map((allocation: any) => ({
            ...allocation.fund,
            allocation: allocation.allocation,
            totalFundCharge: calculateTotalFundCharge(allocation.fund),
            type: 'f'
          }))
        );
        return;
      }

      setParentRowData([]);
      setRowData(params.row.original.fundAllocations);
    }
  };

  const reset = (key: string) => {
    resetTableState(key);
    setCount(count + 1);
  };

  const tableId = `ia_case_${selectedInvestment}_charges_funds_mps`;

  return (
    <div style={{ paddingTop: '2rem' }}>
      <Grid container>
        {data && data.length > 0 && (
          <div style={{ width: '100%' }}>
            <h3>
              Charges
              <span
                className="dashboardLink"
                style={{ float: 'right', marginRight: '0.6rem' }}
                onClick={() => reset(tableId)}
              >
                Reset Filters
              </span>
            </h3>
            <PersistentTable
              key={`${tableId}_${count}`}
              tableId={tableId}
              columns={columns}
              data={data}
              rowClick={handleRowClick}
            />
          </div>
        )}
        <Modal isOpen={modalOpen} closeModalFunc={() => setModalOpen(false)}>
          <BoxWrapper
            title={`${selectedMpName} -  Model Portfolio Allocations`}
            closeModal={() => {
              setModalOpen(false);
            }}
            style={{ width: '90vw' }}
          >
            <div style={{ padding: '0 1rem 1rem 1rem' }}>
              {modelPortfolioFunds.length > 0 && (
                <>
                  <h3>Funds</h3>
                  <div style={{ maxHeight: '456px' }}>
                    <PersistentTable
                      tableId="overview_mpfunds"
                      columns={mpColumns}
                      data={modelPortfolioFunds}
                      height="400px"
                    />
                  </div>
                </>
              )}
              {nestedModelPortfolios.length > 0 && (
                <div style={{ marginTop: '2rem' }}>
                  <h3>Portfolios</h3>
                  <div style={{ maxHeight: '456px' }}>
                    <PersistentTable
                      tableId="overview_mp"
                      columns={mpColumns}
                      data={nestedModelPortfolios}
                      height="400px"
                      rowClick={(e: any) => handleRowClick(e, true)}
                    />
                  </div>
                </div>
              )}
              <div style={{ margin: '1rem 0' }}>
                <Button
                  color="primary"
                  onClick={() =>
                    parentRowData.length > 0
                      ? goBackToParentMp()
                      : setModalOpen(false)
                  }
                >
                  {parentRowData.length > 0 ? 'Back' : 'Cancel'}
                </Button>
              </div>
            </div>
          </BoxWrapper>
        </Modal>
      </Grid>
    </div>
  );
};

export default connect(
  (state: any) => ({
    investments:
      selectors.investmentAnalyserSelectors.researchInvestments(state),
    selectedInvestments:
      selectors.investmentAnalyserSelectors.checkedResearchInvestments(state),
    initialValues: {
      investments:
        selectors.investmentAnalyserSelectors.checkedResearchInvestments(state)
    },
    formValues: getFormValues('researchCharges')(state)
  }),
  {
    updateResearchInvestments:
      actions.investmentAnalyserActions.updateResearchInvestments
  }
)(
  reduxForm({
    form: 'researchCharges'
  })(ChargesTabContent)
);
